// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import shareProduct from "@insite/client-framework/Store/CommonHandlers/ShareProduct";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import { formGridProps } from "@store/Themes/common/props/form";
import getInvalidFieldMessage from "@toolkit/Framework/Common/Utilities/forms/getInvalidFieldMessage";
import getRequiredFieldMessage from "@toolkit/Framework/Common/Utilities/forms/getRequiredFieldMessage";
/* Starter Custom Code - End */

interface OwnProps {
    text?: string;
    extendedStyles?: ProductShareLinkStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProduct;

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

const mapDispatchToProps = {
    shareProduct,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

export interface ProductShareLinkStyles {
    link?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    recipientNameGridItem?: GridItemProps;
    recipientNameTextField?: TextFieldProps;
    recipientEmailGridItem?: GridItemProps;
    recipientEmailTextField?: TextFieldProps;
    yourNameGridItem?: GridItemProps;
    yourNameTextField?: TextFieldProps;
    yourEmailGridItem?: GridItemProps;
    yourEmailTextField?: TextFieldProps;
    yourMessageGridItem?: GridItemProps;
    yourMessageTextArea?: TextAreaProps;
    reCaptchaGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    shareButton?: ButtonPresentationProps;
}

export const productShareLinkStyles: ProductShareLinkStyles = {
    modal: {
        sizeVariant: "medium",
    },
    container: formGridProps.gridContainer,
    recipientNameGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    recipientEmailGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    yourNameGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    yourEmailGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    yourMessageGridItem: {
        width: 12,
    },
    reCaptchaGridItem: {
        width: 12,
    },
    cancelButton: {
        variant: "secondary",
    },
};

const ProductShareLink = ({
    product,
    productInfo,
    text,
    session,
    shareProduct,
    validateReCaptcha,
    extendedStyles,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(productShareLinkStyles, extendedStyles));
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [recipientName, setRecipientName] = React.useState("");
    const [recipientNameError, setRecipientNameError] = React.useState("");
    const [recipientEmail, setRecipientEmail] = React.useState("");
    const [recipientEmailError, setRecipientEmailError] = React.useState("");
    const initialYourName = isAuthenticated
        ? `${session!.firstName} ${session!.lastName}`.trim() || session!.userName || ""
        : "";
    const [yourName, setYourName] = React.useState(initialYourName);
    const [yourNameError, setYourNameError] = React.useState("");
    const initialYourEmail = isAuthenticated ? session.email || "" : "";
    const [yourEmail, setYourEmail] = React.useState(initialYourEmail);
    const [yourEmailError, setYourEmailError] = React.useState("");
    const [yourMessage, setYourMessage] = React.useState("");
    const [yourMessageError, setYourMessageError] = React.useState("");
    const [inProgress, setInProgress] = React.useState(false);
    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();

    /* Starter Custom Code - Start */
    const recipientEmailLabel = translate("Recipient Email");
    const yourEmailLabel = translate("Your Email");
    const recipientNameLabel = translate("Recipient Name");
    const yourNameLabel = translate("Your Name");
    const yourMessageLabel = translate("Your Message");
    /* Starter Custom Code - End */

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const shareLinkClickHandler = () => {
        resetFields();
        setModalIsOpen(true);
    };

    // Starter Removed - requiredFieldMessage
    // Starter Removed - emailFieldMessage
    const checkForErrors = () => {
        const localRecipientNameError = !recipientName ? getRequiredFieldMessage(recipientNameLabel) : ""; // Starter Custom Code
        setRecipientNameError(localRecipientNameError);
        const localRecipientEmailError = !recipientEmail
            ? getRequiredFieldMessage(recipientEmailLabel) // Starter Custom Code
            : validateEmail(recipientEmail)
              ? ""
              : getInvalidFieldMessage(recipientEmailLabel); // Starter Custom Code
        setRecipientEmailError(localRecipientEmailError);
        const localYourNameError = !yourName ? getRequiredFieldMessage(yourNameLabel) : ""; // Starter Custom Code
        setYourNameError(localYourNameError);
        const localYourEmailError = !yourEmail
            ? getRequiredFieldMessage(yourEmailLabel) // Starter Custom Code
            : validateEmail(yourEmail)
              ? ""
              : getInvalidFieldMessage(yourEmailLabel); // Starter Custom Code
        setYourEmailError(localYourEmailError);
        const localYourMessageError = !yourMessage ? getRequiredFieldMessage(yourMessageLabel) : ""; // Starter Custom Code
        setYourMessageError(localYourMessageError);

        return !!(
            localRecipientNameError ||
            localRecipientEmailError ||
            localYourNameError ||
            localYourEmailError ||
            localYourMessageError
        );
    };

    const resetFields = () => {
        setRecipientName("");
        setRecipientNameError("");
        setRecipientEmail("");
        setRecipientEmailError("");
        setYourName(initialYourName);
        setYourNameError("");
        setYourEmail(initialYourEmail);
        setYourEmailError("");
        setYourMessage("");
        setYourMessageError("");
        setInProgress(false);
    };

    const recipientNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRecipientName(event.target.value);
        setRecipientNameError(!event.target.value.trim() ? getRequiredFieldMessage(recipientNameLabel) : ""); // Starter Custom Code
    };
    const recipientEmailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRecipientEmail(event.target.value);
        setRecipientEmailError(!event.target.value.trim() ? getRequiredFieldMessage(recipientEmailLabel) : ""); // Starter Custom Code
    };
    const yourNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setYourName(event.target.value);
        setYourNameError(!event.target.value.trim() ? getRequiredFieldMessage(yourNameLabel) : ""); // Starter Custom Code
    };
    const yourEmailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setYourEmail(event.target.value);
        setYourEmailError(!event.target.value.trim() ? getRequiredFieldMessage(yourEmailLabel) : ""); // Starter Custom Code
    };
    const yourMessageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setYourMessage(event.target.value);
        setYourMessageError(!event.target.value.trim() ? getRequiredFieldMessage(yourMessageLabel) : ""); // Starter Custom Code
    };

    const shareButtonClickHandler = async () => {
        if (checkForErrors()) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({
            location: "ShareProduct",
            recaptchaRef,
        });

        if (!isReCaptchaValid) {
            return;
        }

        setInProgress(true);

        shareProduct({
            product,
            productDetailPath: productInfo.productDetailPath,
            friendsName: recipientName,
            friendsEmailAddress: recipientEmail,
            yourName,
            yourEmailAddress: yourEmail,
            yourMessage,
            onSuccess: () => {
                setModalIsOpen(false);
                toasterContext.addToast({ body: siteMessage("TellAFriend_Success"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                }
            },
        });
    };

    return (
        <>
            <Link {...styles.link} onClick={shareLinkClickHandler} data-test-selector="productShareLink">
                {text || translate("Share")}
            </Link>
            <Modal
                {...styles.modal}
                headline={translate("Share Product")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <FormWrapper onlyFocus /* Starter Custom Code */>
                    <GridContainer {...styles.container} data-test-selector="productShareModal">
                        <GridItem {...styles.recipientNameGridItem}>
                            <TextField
                                label={recipientNameLabel} // Starter Custom Code
                                value={recipientName}
                                error={recipientNameError}
                                onChange={recipientNameChangeHandler}
                                data-test-selector="productShareRecipientName"
                                required // Starter Custom Code
                                {...styles.recipientNameTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.recipientEmailGridItem}>
                            <TextField
                                label={recipientEmailLabel} // Starter Custom Code
                                value={recipientEmail}
                                error={recipientEmailError}
                                onChange={recipientEmailChangeHandler}
                                data-test-selector="productShareRecipientEmail"
                                maxLength={254}
                                required // Starter Custom Code
                                {...styles.recipientEmailTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.yourNameGridItem}>
                            <TextField
                                label={yourNameLabel} // Starter Custom Code
                                value={yourName}
                                error={yourNameError}
                                onChange={yourNameChangeHandler}
                                data-test-selector="productShareYourName"
                                required // Starter Custom Code
                                {...styles.yourNameTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.yourEmailGridItem}>
                            <TextField
                                label={yourEmailLabel} // Starter Custom Code
                                value={yourEmail}
                                error={yourEmailError}
                                onChange={yourEmailChangeHandler}
                                data-test-selector="productShareYourEmail"
                                required // Starter Custom Code
                                {...styles.yourEmailTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.yourMessageGridItem}>
                            <TextArea
                                label={yourMessageLabel} // Starter Custom Code
                                value={yourMessage}
                                error={yourMessageError}
                                onChange={yourMessageChangeHandler}
                                data-test-selector="productShareYourMessage"
                                required // Starter Custom Code
                                {...styles.yourMessageTextArea}
                            />
                        </GridItem>
                    </GridContainer>
                    {/* Starter Custom Code - Start */}
                    <ReCaptcha recaptchaRef={recaptchaRef} location="ShareProduct" />
                    <FormActions>
                        <Button
                            {...styles.shareButton}
                            onClick={shareButtonClickHandler}
                            data-test-selector="productShareSubmit"
                            disabled={inProgress}
                        >
                            {translate("Share")}
                        </Button>
                        <Button
                            {...styles.cancelButton}
                            onClick={modalCloseHandler}
                            data-test-selector="productShareCancel"
                        >
                            {translate("Cancel")}
                        </Button>
                    </FormActions>
                    {/* Starter Custom Code - End */}
                </FormWrapper>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductShareLink));
